import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService, TuiDialogContext } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { switchMap } from 'rxjs';

import { ErrorService } from '@common/services/error.service';
import { matchFieldsValidator } from '@common/utils/validators/match-field-validator';
import { UsersService } from '@services/users.service';

import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.less'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ChangePasswordComponent {
  protected readonly translate = inject(TranslateService);
  private readonly context = inject<TuiDialogContext<any, any>>(POLYMORPHEUS_CONTEXT);
  private readonly alerts = inject<TuiAlertService>(TuiAlertService);
  private readonly userService = inject(UsersService);
  private readonly errorHandler = inject(ErrorService);
  private readonly destroyRef = inject(DestroyRef);

  formGroup: FormGroup = new FormGroup({
    oldPassword: new FormControl(null, [Validators.required]),
    newPassword: new FormControl(null, [Validators.required, Validators.minLength(6)]),
    confirmPassword: new FormControl(null, [
      Validators.required,
      matchFieldsValidator('newPassword'),
    ]),
  });

  constructor() {
    this.formGroup
      .get('newPassword')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formGroup.get('confirmPassword')?.updateValueAndValidity();
      });
  }

  onSave() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.userService.selfChangePassword$.next(true);

    this.userService
      .changePassword(this.formGroup.value)
      .pipe(
        switchMap((res: any) => {
          sessionStorage.setItem(environment.tokenKey, res.token);
          this.context.completeWith(true);
          return this.alerts.open(this.translate.instant('Password changed successfully'));
        }),
      )
      .subscribe({
        next: () => {
          // успех обрабатывать не нужно, важна только ветка ошибки
        },
        error: (error: any) => {
          this.userService.selfChangePassword$.next(false);
          this.errorHandler.simpleErrorHandler(error, this.formGroup);
        },
      });
  }

  onCancel() {
    this.context.completeWith(false);
  }
}
